import axios, { type AxiosRequestConfig } from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Reserved for callers that handle 401 locally (e.g. share dialog, background share list refresh). */
    skipAuthRedirect?: boolean;
    /** Skip document-activity audit rows for this request (undo/redo stack corrections). */
    suppressDocumentActivity?: boolean;
  }
}

let suppressDocumentActivityDepth = 0;

/** Wrap undo/redo (and similar stack replays) so API mutations are not logged as new user edits. */
export const withSuppressedDocumentActivity = async <T>(fn: () => Promise<T>): Promise<T> => {
  suppressDocumentActivityDepth += 1;
  try {
    return await fn();
  } finally {
    suppressDocumentActivityDepth -= 1;
  }
};

/** Use on background refresh/poll requests so a stray 401 does not log the user out of the whole app. */
export const skipAuthRedirectConfig = (): AxiosRequestConfig => ({ skipAuthRedirect: true });

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in cross-origin requests
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Extract a user-visible message from axios error response bodies (string or ErrorResponse JSON). */
export function formatApiErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (data && typeof data === 'object') {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
}

/**
 * Decide whether an error should bounce the user to /login.
 * Only a 401 (authentication/session failure) triggers this, and only for
 * foreground calls that did not opt out via skipAuthRedirect.
 */
const shouldRedirectToLogin = (error: any): boolean => {
  if (error?.response?.status !== 401) return false;
  if (error?.config?.skipAuthRedirect) return false;

  // Never redirect for auth endpoints themselves (login/register/status checks)
  // to avoid masking real auth errors or creating redirect loops.
  const url: string = error?.config?.url || '';
  if (url.includes('/auth/')) return false;

  // Already on the login page – nothing to do.
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return false;
  }
  return true;
};

let redirectingToLogin = false;
const redirectToLogin = () => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('adminTokenBackup');
    // Flag read once by the login page to explain why the user landed there.
    sessionStorage.setItem('sessionExpired', '1');
  } catch {
    // ignore storage failures
  }
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const shouldRetryDocumentLock = (error: any): boolean => {
  const status = error?.response?.status;
  if (status !== 409) return false;

  const method = (error?.config?.method || '').toLowerCase();
  if (!method || method === 'get') return false;

  const url: string = error?.config?.url || '';
  // Only auto-retry for document mutation endpoints.
  if (!url.includes('/categories') && !url.includes('/content')) return false;

  const msg: string =
    (typeof error?.response?.data === 'string' && error.response.data) ||
    error?.response?.data?.message ||
    '';
  return msg.toLowerCase().includes('document is currently being saved');
};

// Add response interceptor to handle token validation + document-lock retries
api.interceptors.response.use(
  (response) => {
    // Sliding session: the backend hands us a fresh token while the user is active.
    // Swap it into storage so subsequent requests carry the extended token.
    const refreshed = response.headers?.['x-refresh-token'];
    if (typeof refreshed === 'string' && refreshed) {
      try {
        localStorage.setItem('token', refreshed);
      } catch {
        // ignore storage failures
      }
    }
    return response;
  },
  async (error) => {
    if (shouldRetryDocumentLock(error)) {
      const config = error.config;
      const currentRetryCount = (config.__documentLockRetryCount || 0) as number;
      const maxRetries = 12; // ~ up to a few seconds worst-case

      if (currentRetryCount < maxRetries) {
        config.__documentLockRetryCount = currentRetryCount + 1;
        // Exponential backoff with cap.
        const delayMs = Math.min(2000, 150 * Math.pow(1.4, currentRetryCount));
        await sleep(delayMs);
        return api.request(config);
      }
    }

    // Expired/invalid token (or any unauthenticated request): clear auth and
    // send the user to the login page so they don't sit on a silently broken app.
    // Return a promise that never settles so callers don't flash their own error
    // toast in the moment before the page navigates away.
    if (shouldRedirectToLogin(error)) {
      redirectToLogin();
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Default instance uses application/json; FormData must use multipart with a boundary (set by the browser).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (config.suppressDocumentActivity || suppressDocumentActivityDepth > 0) {
      config.headers['X-Suppress-Document-Activity'] = 'true';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api; 